import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFlight } from '../contexts/FlightContext';
import { UsersIcon, QuestionMarkCircleIcon, BellIcon, BellSlashIcon } from './Icons';
import HelpModal, { NotificationHelpModal } from './HelpModal';

const TopBar: React.FC = () => {
  const { user, activeUsers, subscribeToPushNotifications, unsubscribeFromPushNotifications } = useAuth();
  const { areNotificationsEnabled, toggleNotifications, isAudioUnlocked, showNotification } = useFlight();
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showNotificationHelp, setShowNotificationHelp] = useState(false);
  const [pushPermission, setPushPermission] = useState<NotificationPermission>(Notification.permission);

  useEffect(() => {
    // Periodically check for changes in notification permission state
    const interval = setInterval(() => {
        if (Notification.permission !== pushPermission) {
            setPushPermission(Notification.permission);
        }
    }, 1000);
    return () => clearInterval(interval);
  }, [pushPermission]);
  
  const handleSubscribe = useCallback(async () => {
    const result = await subscribeToPushNotifications();
    if (result.success) {
        showNotification('Push notifications enabled successfully!', 'success');
    } else {
        if (result.reason === 'missing_key') {
            showNotification('Push notifications are not configured by the admin.', 'error');
        } else if (result.reason === 'denied') {
            showNotification('Permission for notifications was denied.', 'error');
            setShowNotificationHelp(true);
        } else {
            showNotification('Failed to enable push notifications.', 'error');
        }
    }
    // Manually refresh permission state after action attempt
    setPushPermission(Notification.permission);
  }, [subscribeToPushNotifications, showNotification]);

  const handleUnsubscribe = useCallback(async () => {
    const success = await unsubscribeFromPushNotifications();
    if (success) {
        showNotification('Push notifications disabled.', 'success');
    } else {
        showNotification('Failed to disable push notifications.', 'error');
    }
    setPushPermission(Notification.permission);
  }, [unsubscribeFromPushNotifications, showNotification]);


  const audioNotificationState = useMemo(() => {
    if (!areNotificationsEnabled) {
      return {
        title: 'Unmute In-App Sounds',
        icon: <BellSlashIcon className="w-5 h-5" />,
      };
    }
    if (!isAudioUnlocked) {
      return {
        title: 'Sound muted by browser. Click anywhere on the page to enable.',
        icon: <BellIcon className="w-5 h-5 animate-intense-pulse text-amber-400" />,
      };
    }
    return {
      title: 'Mute In-App Sounds',
      icon: <BellIcon className="w-5 h-5" />,
    };
  }, [areNotificationsEnabled, isAudioUnlocked]);

  const pushNotificationState = useMemo(() => {
    switch(pushPermission) {
        case 'granted':
            return { title: 'Disable Push Notifications', icon: <BellIcon className="w-5 h-5 text-green-400" />, action: handleUnsubscribe };
        case 'denied':
            return { title: 'Push notifications are blocked. Click for instructions.', icon: <BellSlashIcon className="w-5 h-5 text-red-400" />, action: () => setShowNotificationHelp(true) };
        default: // 'default'
            return { title: 'Enable Push Notifications', icon: <BellIcon className="w-5 h-5 animate-pulse" />, action: handleSubscribe };
    }
  }, [pushPermission, handleSubscribe, handleUnsubscribe]);


  return (
    <>
      <div className="bg-slate-950 text-slate-400 text-xs no-print pt-[env(safe-area-inset-top)]">
        <div className="container mx-auto flex justify-between items-center px-2 sm:px-4 py-1">
          <div>
            {user && <span>Welcome, <span className="font-bold bg-gradient-to-r from-blue-400 to-cyan-300 text-transparent bg-clip-text">{user.username}</span></span>}
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative group">
              <div className="flex items-center space-x-2 cursor-pointer p-1">
                <UsersIcon className="w-4 h-4 text-green-400" />
                <span className="font-bold">{activeUsers.length} Active User{activeUsers.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="absolute top-full right-0 mt-1 w-48 bg-slate-800 border border-slate-700 rounded-lg shadow-lg z-20 py-2 hidden group-hover:block animate-slide-up-fade-in">
                  <p className="px-3 py-1 font-bold text-white border-b border-slate-700 mb-1">Active Users</p>
                  {activeUsers.length > 0 ? (
                    <ul>
                      {activeUsers.map(u => <li key={u.id} className="px-3 py-1 text-sm text-slate-300">{u.username}</li>)}
                    </ul>
                  ) : (
                    <p className="px-3 py-1 text-sm text-slate-400">No other users active.</p>
                  )}
              </div>
            </div>
             <button
              onClick={pushNotificationState.action}
              className="text-slate-400 hover:text-white p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-blue-500"
              title={pushNotificationState.title}
            >
              {pushNotificationState.icon}
            </button>
            <button
              onClick={toggleNotifications}
              className="text-slate-400 hover:text-white p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-blue-500"
              title={audioNotificationState.title}
            >
              {audioNotificationState.icon}
            </button>
            <button
              onClick={() => setShowHelpModal(true)}
              className="text-slate-400 hover:text-white p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-blue-500"
              title="Help"
            >
              <QuestionMarkCircleIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
      {showHelpModal && <HelpModal onClose={() => setShowHelpModal(false)} />}
      {showNotificationHelp && <NotificationHelpModal onClose={() => setShowNotificationHelp(false)} />}
    </>
  );
};

export default TopBar;